const guides = {
  eu_ad_personalization_required: {
    hu: `## Mi a probléma?

Az EU-s adatvédelmi szabályozások miatt a rendszer nem enged hirdetést létrehozni vagy módosítani, ha a fiókodban a "Kevésbé személyre szabott" mód van kiválasztva. A hirdetések kezeléséhez engedélyezned kell a teljes hirdetési élményt.

## Megoldás 3 lépésben:

1. Nyisd meg a Facebook Fiókközpontot ezen a közvetlen linken: [https://accountscenter.facebook.com/ads](https://accountscenter.facebook.com/ads)
2. Az oldalon kattints az **"Adatok kezelése"** (Manage info) fülre, majd kattints a **"Hirdetésekkel kapcsolatos élmény"** (Ad experience) sorra.
3. A megjelenő ablakban válaszd a **"Személyre szabott hirdetések"** (Personalized ads) opciót, majd mentsd el.

**Fontos:** Ha Instagram-fiók is kapcsolódik a hirdetéseidhez, kérjük, ellenőrizd ugyanezt a beállítást az Instagram alkalmazás Fiókközpontjában is.`,

    en: `## What is the problem?

Due to EU data protection regulations, the system does not allow creating or modifying ads if your account has the "Less personalized" mode selected. You need to enable the full ad experience to manage ads.

## Solution in 3 steps:

1. Open the Facebook Account Center using this direct link: [https://accountscenter.facebook.com/ads](https://accountscenter.facebook.com/ads)
2. On the page, click the **"Manage info"** tab, then click the **"Ad experience"** row.
3. In the dialog that appears, select the **"Personalized ads"** option and save.

**Important:** If an Instagram account is also linked to your ads, please verify the same setting in the Instagram app's Account Center as well.`,
  },

  login_checkpoint_required: {
    hu: `## Mi a probléma?

A Meta **biztonsági ellenőrzés alá vonta a személyes Facebook-fiókodat**. Amíg ezt el nem végzed, a rendszer egyetlen hirdetési adatodhoz sem fér hozzá: nem tud új hirdetést létrehozni, és a meglévők adatait sem tudja frissíteni.

Fontos: ez nem a hirdetési fiókod korlátozása, hanem a Te személyes fiókodé. Ezért a hozzá kapcsolt **összes** hirdetési fiók és oldal egyszerre érintett. A már futó hirdetéseid ettől még futnak tovább.

## Megoldás 3 lépésben:

1. Nyisd meg a [https://www.facebook.com](https://www.facebook.com) oldalt, és jelentkezz be azzal a fiókkal, amelyik a hirdetési fiókot kezeli.
2. Belépés után a Facebook automatikusan megjeleníti az ellenőrzést. Kövesd a kért lépéseket - ez fiókonként eltérő lehet: jelszó megerősítése, kétlépcsős azonosítás, eszköz felismerése vagy személyazonosság igazolása.
3. Ha nem jelenik meg semmi, nyisd meg a **Beállítások és adatvédelem → Beállítások → Biztonság és bejelentkezés** oldalt, és nézd meg, van-e ott elintézendő teendő.

**Fontos:** Miután végeztél, nincs teendőd az alkalmazásban. A rendszer a következő adatfrissítésnél magától észreveszi, hogy megszűnt az akadály, és folytatja a munkát.`,

    en: `## What is the problem?

Meta has **put a security check on your personal Facebook account**. Until you complete it, the system cannot reach any of your advertising data: it can neither create new ads nor refresh the data of existing ones.

Important: this is not a restriction on your ad account, but on your personal account. That is why **every** ad account and page connected to it is affected at once. Your already running ads keep running regardless.

## Solution in 3 steps:

1. Open [https://www.facebook.com](https://www.facebook.com) and sign in with the account that manages the ad account.
2. After signing in, Facebook shows the check automatically. Follow the steps it asks for - these differ from account to account: password confirmation, two-factor authentication, device recognition or identity verification.
3. If nothing appears, open **Settings & privacy → Settings → Security and login** and see whether there is anything waiting for you there.

**Important:** Once you are done, there is nothing to do in the application. The system notices on its next data refresh that the obstacle is gone, and carries on.`,
  },

  account_authentication_required: {
    hu: `## Mi a probléma?

A Meta biztonsági rendszere illetéktelen hozzáférési kísérletet gyanít a hirdetési fiókodnál, ezért **azonosítást kér a fiók tulajdonosától**. Amíg ez nem történik meg, új hirdetés nem hozható létre és a meglévők nem módosíthatók. A már futó hirdetéseidet ez nem érinti, azok tovább futnak.

Ezt az azonosítást csak Te tudod elvégezni a Meta felületén, a rendszer nem tudja helyetted megtenni.

## Megoldás 3 lépésben:

1. Nyisd meg a Hirdetéskezelőt ezen a linken: [https://adsmanager.facebook.com](https://adsmanager.facebook.com) — jelentkezz be azzal a fiókkal, amelyik a hirdetési fiókot kezeli.
2. A felület tetején megjelenő értesítésben kattints az azonosítás elindítására, majd kövesd a Meta által kért lépéseket (jelszó megerősítése, kétlépcsős azonosítás vagy személyazonosság igazolása - ez fiókonként eltérő).
3. Ha nem látod az értesítést, nyisd meg a **Fiókminőség** oldalt: [https://business.facebook.com/accountquality](https://business.facebook.com/accountquality), és keresd meg az érintett hirdetési fiókot - itt is megjelenik a szükséges teendő.

**Fontos:** Az azonosítás elvégzése után a rendszer a következő futásnál automatikusan újrapróbálja a hirdetés létrehozását, nincs teendőd az alkalmazásban.`,

    en: `## What is the problem?

Meta's security system suspects an unauthorized access attempt on your ad account, so it **asks the account owner to authenticate**. Until that happens, no new ads can be created and existing ones cannot be modified. Your already running ads are not affected, they keep running.

Only you can complete this authentication on Meta's interface, the system cannot do it on your behalf.

## Solution in 3 steps:

1. Open Ads Manager using this link: [https://adsmanager.facebook.com](https://adsmanager.facebook.com) — sign in with the account that manages the ad account.
2. In the notification shown at the top of the interface, start the authentication and follow the steps Meta asks for (password confirmation, two-factor authentication or identity verification - this differs from account to account).
3. If you do not see the notification, open the **Account Quality** page: [https://business.facebook.com/accountquality](https://business.facebook.com/accountquality) and find the affected ad account - the required action appears there as well.

**Important:** Once the authentication is done, the system retries the ad creation automatically on its next run, there is nothing to do in the application.`,
  },

  verified_advertiser_required: {
    hu: `## Mi a probléma?

A Meta ennél a hirdetési fióknál minden új hirdetéskészlethez ellenőrzött hirdetőt kér, ezért a rendszer nem tud új hirdetéskészletet létrehozni. Az ellenőrzött hirdetőt a Meta nem adja ki közvetlenül, ezért a rendszer a fiók egy már meglévő hirdetéskészletéről veszi át – de jelenleg egyiken sincs beállítva.

## Megoldás 3 lépésben:

1. Nyisd meg a Hirdetéskezelőt: [https://adsmanager.facebook.com](https://adsmanager.facebook.com), és válaszd ki ezt a hirdetési fiókot.
2. Hozz létre egy új hirdetéskészletet bármelyik kampányban, és a hirdetéskészlet beállításainál válaszd ki az ellenőrzött hirdetőt a **kedvezményezett és a finanszírozó** megadásánál. Ha még nincs ellenőrzött hirdetőd, a Meta ugyanitt végigvezet az ellenőrzésen.
3. Tedd közzé a hirdetéskészletet. Futnia nem kell, szüneteltetheted is.

**Fontos:** A rendszer a következő futáskor automatikusan átveszi az ellenőrzött hirdetőt, és onnantól magától használja – az alkalmazásban nincs teendőd. A hirdetéskészletet addig ne töröld, és piszkozatként se hagyd, mert azt a rendszer nem látja.`,

    en: `## What is the problem?

For this ad account, Meta asks for a verified advertiser on every new ad set, so the system cannot create new ad sets. Meta does not hand out the verified advertiser directly, so the system takes it over from an existing ad set of the account – but none of them has one set at the moment.

## Solution in 3 steps:

1. Open Ads Manager: [https://adsmanager.facebook.com](https://adsmanager.facebook.com), and select this ad account.
2. Create a new ad set in any campaign, and in the settings of the ad set select the verified advertiser where the **beneficiary and the payer** are declared. If you have no verified advertiser yet, Meta guides you through the verification right there.
3. Publish the ad set. It does not need to run, you can pause it.

**Important:** On its next run, the system takes over the verified advertiser automatically and uses it from then on – there is nothing to do in the application. Do not delete the ad set until then, and do not leave it as a draft either, because the system cannot see drafts.`,
  },
};

export function getGuideMarkdown(interventionCode, language) {
  const guide = guides[interventionCode];
  if (!guide) {
    return null;
  }

  return guide[language] || guide['en'] || null;
}
